import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { RadiatorElement, RadiatorType } from '../types';
import { Camera, X, Image, ChevronLeft, ChevronRight } from 'lucide-react';
import PhotoCapture from './PhotoCapture';
import PhotoGalleryModal from './PhotoGalleryModal';
import { PhotoData } from '../services/CameraService';
import CameraService from '../services/CameraService';

interface RadiatorPhoto {
  id: string;
  fileName: string;
  localPath: string;
  url: string;
  timestamp: string;
  order: number;
}

interface RadiatorDimensionPromptProps {
  isOpen: boolean;
  radiatorElement: RadiatorElement | null;
  onSave: (dimensions: { width: number; height: number; radiatorType: RadiatorType; photos?: RadiatorPhoto[]; }) => void;
  onDelete: () => void;
  onClose: () => void;
  isFullscreen?: boolean;
}

const RadiatorDimensionPrompt: React.FC<RadiatorDimensionPromptProps> = ({ isOpen, radiatorElement, onSave, onDelete, onClose, isFullscreen = false }) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [radiatorType, setRadiatorType] = useState<RadiatorType>('with-trvs');
  const [photos, setPhotos] = useState<RadiatorPhoto[]>([]);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  useEffect(() => {
    if (radiatorElement) {
      // Always start with empty fields for new radiator placement
      setStep(1);
      setWidth('');
      setHeight('');
      setRadiatorType('with-trvs');
      setPhotos([]);
    }
  }, [radiatorElement]);

  // Handle photo capture
  const handlePhotosCaptured = (newPhotos: PhotoData[]) => {
    console.log(`[RadiatorModal] Photos captured: ${newPhotos.length}`);
    
    // Convert PhotoData to RadiatorPhoto format
    const radiatorPhotos: RadiatorPhoto[] = newPhotos.map(photo => ({
      id: `radiator-photo-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      fileName: `radiator-photo-${Date.now()}-${Math.random().toString(36).substring(2, 9)}.jpg`,
      localPath: `data:image/${photo.format};base64,${photo.base64String}`,
      url: `data:image/${photo.format};base64,${photo.base64String}`,
      timestamp: new Date().toISOString(),
      order: photos.length
    }));
    
    // Add to existing photos
    setPhotos(prev => [...prev, ...radiatorPhotos]);
  };

  // Remove photo
  const removePhoto = (photoId: string) => {
    setPhotos(prev => prev.filter(photo => photo.id !== photoId));
  };

  // Handle next step
  const handleNextStep = () => {
    if (!width || !height) {
      alert('Please enter radiator dimensions');
      return;
    }
    setStep(2);
  };

  // Handle delete photo (for gallery)
  const handleDeletePhoto = (photoId: string) => {
    setPhotos(prev => prev.filter(photo => photo.id !== photoId));
  };

  // Handle update note (for gallery)
  const handleUpdateNote = (photoId: string, note: string) => {
    setPhotos(prev => prev.map(photo =>
      photo.id === photoId ? { ...photo, note } as RadiatorPhoto : photo
    ));
  };

  const handleSkip = () => {
    onSave({
      width: parseFloat(width) || 0,
      height: parseFloat(height) || 0,
      radiatorType: radiatorType,
      photos: []
    });
    onClose();
  };

  const handleDelete = () => {
    onDelete();
    onClose();
  };

  // Render camera button
  const renderCameraButton = () => {
    const cameraService = CameraService.getInstance();
    const supportsCustomCamera = cameraService.supportsCustomCamera();
    
    if (supportsCustomCamera) {
      return (
        <PhotoCapture
          onPhotosCaptured={handlePhotosCaptured}
          maxPhotos={10}
          buttonLabel="Take Photos"
          buttonClass="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-lg shadow-blue-500/25 transition-all duration-200"
        />
      );
    } else {
      return (
        <button
          type="button"
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-lg shadow-blue-500/25 transition-all duration-200"
          onClick={() => console.warn('Camera not available on this device')}
        >
          <Camera className="w-4 h-4" />
          <span>Take Photos</span>
        </button>
      );
    }
  };

  if (!isOpen) {
    return null;
  }

  const handleSave = () => {
    onSave({
      width: parseFloat(width) || 0,
      height: parseFloat(height) || 0,
      radiatorType: radiatorType,
      photos: photos
    });
    onClose();
  };

  const formatDecimalInput = (value: string): string => {
    if (value === '') return '';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '';
    return numValue.toString();
  };

  // Modern dropdown component with icons and enhanced styling
  const ModernDropdown = ({ label, value, options, onChange, icon }: {
    label: string;
    value: string;
    options: Array<{value: string; label: string}>;
    onChange: (value: string) => void;
    icon?: React.ReactNode;
  }) => (
    <div className="space-y-1.5">
      <label className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
        {icon && <span className="text-slate-500">{icon}</span>}
        <span>{label}</span>
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2.5 text-sm bg-white border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all duration-200 appearance-none cursor-pointer hover:border-slate-300 shadow-sm"
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center px-2.5 pointer-events-none">
          <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    </div>
  );

  const radiatorTypeOptions = [
    { value: 'with-trvs', label: 'With TRVs' },
    { value: 'without-trvs', label: 'Without TRVs' },
    { value: 'panel-heater', label: 'Electric Panel Heater (PH)' },
    { value: 'fan-heater', label: 'Fan Heater (FH)' },
    { value: 'electric-storage-heater', label: 'Electric Storage Heater (ESH)' },
    { value: 'high-heat-retention', label: 'High Heat Retention (HHR)' }
  ];

  if (!isOpen) return null;

  const modalContent = (
    <div className={`fixed inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/60 to-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 ${isFullscreen ? 'z-[10003]' : 'z-[999998]'}`}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-hidden border border-slate-200/50">
        {/* Modern Header with Gradient */}
        <div className="relative bg-gradient-to-r from-orange-600 via-orange-500 to-red-600 px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2.5">
              <div className="p-1.5 bg-white/20 rounded-lg backdrop-blur-sm">
                {step === 1 ? (
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                ) : (
                  <Camera className="w-5 h-5 text-white" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">
                  {step === 1 ? 'Radiator Details' : 'Radiator Photos'}
                </h3>
                <p className="text-orange-100 text-xs">
                  {step === 1 ? 'Configure specifications' : 'Capture radiator images'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="bg-white/20 text-white px-2.5 py-1 rounded-lg text-xs font-semibold backdrop-blur-sm">
                Floor {radiatorElement?.floor}
              </span>
            </div>
          </div>
          {/* Step Indicator */}
          <div className="flex items-center space-x-2 mt-3">
            <div className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${
              step >= 1 ? 'bg-white' : 'bg-white/30'
            }`}></div>
            <div className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${
              step >= 2 ? 'bg-white' : 'bg-white/30'
            }`}></div>
          </div>
          {/* Decorative gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-orange-600/0 via-white/5 to-red-600/0 pointer-events-none"></div>
        </div>

        {/* Content Area */}
        <div className="px-5 py-5 space-y-5 max-h-[calc(85vh-180px)] overflow-y-auto custom-scrollbar">
          {/* Step 1: Radiator Configuration */}
          {step === 1 && (
            <>
              {/* Dimensions Section */}
              <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
            <h4 className="text-xs font-bold text-slate-800 mb-3 flex items-center">
              <svg className="w-3.5 h-3.5 mr-1.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
              Dimensions
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label htmlFor="radiator-length" className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
                  <svg className="w-3 h-3 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                  <span>Length (mm)</span>
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  id="radiator-length"
                  value={width}
                  onChange={(e) => setWidth(formatDecimalInput(e.target.value))}
                  placeholder="600"
                  min="100"
                  max="3000"
                  step="10"
                  className="w-full px-3 py-2.5 text-sm bg-white border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all duration-200 hover:border-slate-300 shadow-sm"
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="radiator-height" className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
                  <svg className="w-3 h-3 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7l4-4m0 0l4 4m-4-4v18" />
                  </svg>
                  <span>Height (mm)</span>
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  id="radiator-height"
                  value={height}
                  onChange={(e) => setHeight(formatDecimalInput(e.target.value))}
                  placeholder="600"
                  min="200"
                  max="1000"
                  step="10"
                  className="w-full px-3 py-2.5 text-sm bg-white border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all duration-200 hover:border-slate-300 shadow-sm"
                />
              </div>
            </div>
          </div>

          {/* Radiator Type */}
          <ModernDropdown
            label="Radiator Type"
            value={radiatorType}
            options={radiatorTypeOptions}
            onChange={(value) => setRadiatorType(value as RadiatorType)}
            icon={<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>}
          />
            </>
          )}

          {/* Step 2: Photo Capture */}
          {step === 2 && (
            <>
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                  Capture Radiator Images
                </h4>
                <div className="p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl border border-orange-200">
                  <p className="text-sm text-orange-800">
                    📸 Take photos of the radiator including any TRV controls and labels. This helps document the heating system.
                  </p>
                </div>
              </div>

              {/* Photo Capture Component */}
              <PhotoCapture
                onPhotosCaptured={handlePhotosCaptured}
                buttonLabel="Take Photos"
                maxPhotos={10}
              />

              {/* Captured Images Preview */}
              {photos.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    Captured Images ({photos.length})
                  </h4>
                  <button
                    onClick={() => setIsGalleryOpen(true)}
                    className="relative w-full aspect-video rounded-xl overflow-hidden border-2 border-orange-200 hover:border-orange-400 transition-all group"
                  >
                    <img
                      src={photos[0].url}
                      alt="Radiator"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-all flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="text-2xl font-bold">{photos.length}</div>
                        <div className="text-sm">Click to view all</div>
                      </div>
                    </div>
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modern Action Bar */}
        <div className="px-5 py-4 bg-slate-50 border-t border-slate-200">
          {step === 1 ? (
            <div className="flex justify-end items-center space-x-2">
              <button
                onClick={onClose}
                className="px-3 py-2 text-xs font-semibold text-slate-700 bg-white border-2 border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500/20 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleNextStep}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-gradient-to-r from-orange-600 to-orange-500 rounded-lg hover:from-orange-700 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500/20 shadow-lg shadow-orange-500/25 transition-all duration-200"
              >
                Next
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div className="flex justify-between items-center">
              <button
                onClick={() => setStep(1)}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-700 bg-white border-2 border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500/20 transition-all duration-200"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Back
              </button>
              <div className="flex space-x-2">
                <button
                  onClick={handleSkip}
                  className="px-3 py-2 text-xs font-semibold text-slate-700 bg-white border-2 border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500/20 transition-all duration-200"
                >
                  Skip
                </button>
                <button
                  onClick={handleSave}
                  className={`px-4 py-2 text-xs font-semibold rounded-lg focus:outline-none focus:ring-2 shadow-lg transition-all duration-200 ${
                    photos.length > 0
                      ? 'text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:ring-green-500/20 shadow-green-500/25'
                      : 'text-white bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 focus:ring-orange-500/20 shadow-orange-500/25'
                  }`}
                >
                  Save {photos.length > 0 && `(${photos.length})`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Photo Gallery Modal */}
      {isGalleryOpen && (
        <PhotoGalleryModal
          isOpen={isGalleryOpen}
          onClose={() => setIsGalleryOpen(false)}
          photos={photos.map(photo => ({
            id: photo.id,
            url: photo.url,
            localPath: photo.localPath,
            fileName: photo.fileName
          }))}
          sectionTitle="Radiator Images"
          onDeletePhoto={handleDeletePhoto}
          onUpdateNote={handleUpdateNote}
          zIndex={10000001}
        />
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default RadiatorDimensionPrompt; 